import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BarChartIcon, HomeIcon, PlusIcon, ShieldIcon, UserIcon } from '../components/ui/icons';
import { Target } from 'lucide-react';
import { MobileSheet } from '../components/ui/mobile-sheet';
import { useAuth } from '../context/AuthContext';
import LoginRequireModal from '../components/modal/LoginRequireModal';

/**
 * 헤더
 * - 모바일/웹 반응형 디자인
 *
 * @author hjkim
 * @constructor
 */
const Header = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();
  /**
   * States
   */
  const [isMobileMenuOpen, setIsMobileMenu] = useState(false);
  const [isOpenLoginRequireModal, setIsOpenLoginRequireModal] = useState<boolean>(false);

  /**
   * Handlers
   */
  const navigateToHome = () => {
    navigate('/');
  };

  const navigateToProfilePage = () => {
    if (isLoading) return;
    if (isAuthenticated) {
      navigate('/user/dashboard');
    } else {
      navigate('/user/login');
    }
  };
  const navigateToAdminPage = () => {
    navigate('/admin/dashboard');
  };
  const navigateToMonitor = () => {
    navigate('/admin/monitor');
  };
  const navigateToStrategy = () => {
    navigate('/admin/strategy');
  };
  const handleQuestionClick = () => {
    if (!isAuthenticated) {
      setIsOpenLoginRequireModal(true);
    } else {
      navigate('/question/create');
    }
  };
  const handleCloseLoginRequireModal = () => {
    setIsOpenLoginRequireModal(false);
  };

  return (
    <header className="bg-white border-b sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3">
        <div className="flex items-center justify-between">
          <div role="button" tabIndex={0} className="flex items-center space-x-2" onClick={navigateToHome}>
            <HomeIcon className="w-6 h-7 sm:w-6 sm:h-7 text-primary" />
            <h1 className="text-lg sm:text-xl font-bold text-primary">나만 그래?</h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* Buttons for Desktop */}
            <div className="hidden sm:flex items-center space-x-2">
              <Button onClick={handleQuestionClick} size="sm" className="flex items-center gap-2">
                <PlusIcon className="w-4 h-4" />
                질문하기
              </Button>
            </div>
            {/* 프로필 페이지 이동 */}
            <Button
              type="button"
              onClick={navigateToProfilePage}
              variant="outline"
              size="sm"
              className="hidden sm:flex items-center space-x-2"
              disabled={isLoading}>
              <UserIcon className="w-4 h-4" />
            </Button>
            {/* 관리자 페이지 이동 */}
            {user?.role === 'ADMIN' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={navigateToStrategy}
                  className="hidden sm:flex items-center space-x-2 text-emerald-500">
                  <Target className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={navigateToMonitor}
                  className="hidden sm:flex items-center space-x-2 text-blue-500">
                  <BarChartIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={navigateToAdminPage}
                  className="hidden sm:flex items-center space-x-2 text-red-500">
                  <ShieldIcon />
                </Button>
              </>
            )}
          </div>
          {/* Buttons for Mobile */}
          <div className="sm:hidden flex items-center space-x-2">
            <Button
              type="button"
              onClick={navigateToProfilePage}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              disabled={isLoading}>
              <UserIcon className="w-4 h-4" />
            </Button>
            {user?.role === 'ADMIN' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={navigateToStrategy}
                  className="flex items-center gap-2 text-emerald-500">
                  <Target className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={navigateToMonitor}
                  className="flex items-center gap-2 text-blue-500">
                  <BarChartIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={navigateToAdminPage}
                  className="flex items-center gap-2 text-red-500">
                  <ShieldIcon className="w-4 h-4" />
                </Button>
              </>
            )}
            <Button onClick={handleQuestionClick} size="sm" className="h-9 px-3">
              <PlusIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      {/* Mobile Menu Sheet */}
      <MobileSheet isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenu(false)}>
        <div>
          <div>
            <Button>질문하기</Button>
          </div>
        </div>
      </MobileSheet>
      <LoginRequireModal
        isOpenLoginModal={isOpenLoginRequireModal}
        onCloseLoginRequireModal={handleCloseLoginRequireModal}
      />
    </header>
  );
};

export default Header;
